import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const PassportAlertsCtx = createContext({
  hasExpiredPassports: false,
  expiredOwnerIds: new Set(),
  hasExpiredStaffDocs: false,
  expiredStaffIds: new Set(),
  refresh: () => {},
});

export function PassportAlertsProvider({ children }) {
  const { user } = useAuth();
  const [hasExpiredPassports, setHasExpiredPassports] = useState(false);
  const [expiredOwnerIds, setExpiredOwnerIds] = useState(new Set());
  const [hasExpiredStaffDocs, setHasExpiredStaffDocs] = useState(false);
  const [expiredStaffIds, setExpiredStaffIds] = useState(new Set());

  const check = useCallback(async () => {
    if (!user || !["ops_manager"].includes(user.role)) return;
    try {
      const { data } = await api.get("/alerts/document-expiry-summary");
      const owners = new Set(data?.expired_owner_ids || []);
      const staff = new Set(data?.expired_staff_ids || []);
      setExpiredOwnerIds(owners);
      setHasExpiredPassports(owners.size > 0);
      setExpiredStaffIds(staff);
      setHasExpiredStaffDocs(staff.size > 0);
    } catch (err) {
      // Failure here means the badge stays stale until next 2-min poll.
      // Log so we notice patterns, but don't break the UI.
      console.error("PassportAlertsContext: document-expiry-summary failed", err);
    }
  }, [user]);

  useEffect(() => {
    check();
    const interval = setInterval(check, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [check]);

  return (
    <PassportAlertsCtx.Provider
      value={{
        hasExpiredPassports,
        expiredOwnerIds,
        hasExpiredStaffDocs,
        expiredStaffIds,
        refresh: check,
      }}
    >
      {children}
    </PassportAlertsCtx.Provider>
  );
}

export const usePassportAlerts = () => useContext(PassportAlertsCtx);
